import { useMessages } from '@app/context/MessageContext';
import { OnboardingState, useOnboardingStateMachine } from '@app/context/OnboardingStateMachineContext';
import { NavigationSection } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { Tutorial } from '@components/block';
import { Loading } from '@components/ui';
import { PasswordModal } from '@components/ui/PasswordModal/PasswordModal';
import { QuestionnaireChangedModal } from '@components/ui/QuestionnaireChangedModal/QuestionnaireChangeModal';
import { Button, Tab, Tabs } from '@mui/material';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import Confetti from './Info/Confetti';
import { FullCompletionMessage } from './Messages/FullCompletion';
import { NewServiceMessage } from './Messages/NewService';
import { Roadblock } from './Messages/Roadblock';
import { SectionBody } from './SectionBody';
import { DocumentPaper } from './Topic/Documents/DocumentPaper';
import './route-dashboard.sass';
import { useDashboard } from './useDashboard';

import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import SideMenuList from '@app/routes/Dashboard/SideMenuList.tsx';
import { Chat } from '@components/block/Chat/Chat';
import { Cello } from '@components/ui/Cello/Cello';
import { DpoAssignmentModal } from '@components/ui';
import { EmailModal } from '@components/ui/EmailModal/EmailModal';
import { TextReplacer } from '@components/ui/TextReplacer/TextReplacer';
import { ChangelogSheet } from '@components/ui/AccountMenu/ChangelogSheet';
import { useDpoInfoQuery } from '@dieterApi/dpo/useDpoInfoQuery';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../UserManagement/UserManagementMockData';
import { DpoSectionInfo } from './Dpo/DpoSectionInfo';
import { CelloReminderMessage } from './Messages/CelloReminder';
import { KlausLaunchMessage } from './Messages/KlausLaunch';
import { KlausMergeOfferMessage } from './Messages/KlausMergeOffer';
import { RoadblockDSFA } from './Messages/RoadblockDSFA';
import { SectionInfo } from './SectionInfo';

const ValidTabValues = [
  NavigationSection.Overview,
  NavigationSection.Training,
  NavigationSection.MultiTenant,
  NavigationSection.Dpo,
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { fullcompletion } = useMessages();

  const { user, questionnaires, loading, refetchDpoTasks, isDpoMode, activeCompany } = useUserWithCompany();
  const dashboard = useDashboard();
  const { navigation, setNavigation, navigateDashboard } = useNavigation();
  const { data: dpoInfoData, loading: dpoLoading } = useDpoInfoQuery(activeCompany?.id);
  const [dpoModalDismissed, setDpoModalDismissed] = useState(false);

  const { isMobile, scope } = navigation;
  const { activeSection } = navigation;
  const { currentState } = useOnboardingStateMachine();

  useEffect(() => {
    setNavigation((nav) => void (nav.boom = fullcompletion.isOpen));
  }, [fullcompletion.isOpen]);

  useEffect(() => {
    if (scope !== 'dashboard') {
      setNavigation((nav) => void (nav.scope = 'dashboard'));
    }
    return () => setNavigation((nav) => void (nav.scope = undefined));
  }, []);

  useEffect(() => {
    if (navigation.justFinishedQuestionnaireRemoteId && currentState === OnboardingState.Complete) {
      // open documentPaper modal for the questionnaire that was just finished
      const finishedQuestionnaire = questionnaires.find(
        (q) => q.questionnaireId === navigation.justFinishedQuestionnaireRemoteId
      );

      if (finishedQuestionnaire) {
        // Refetch DPO tasks to ensure cache is fresh with the completed questionnaire
        if (isDpoMode && refetchDpoTasks) {
          refetchDpoTasks();
        }

        setNavigation((nav) => {
          nav.documentPaper = {
            modalOpen: true,
            questionnaire: finishedQuestionnaire,
            index: 0,
          };
        });
      }
    }
  }, [navigation.justFinishedQuestionnaireRemoteId, isDpoMode, refetchDpoTasks]);

  // NOTE: Questionnaire creation/opening for onboarding is now handled by OnboardingStateMachineProvider

  // NOTE: Dashboard lock enforcement for onboarding is now handled by OnboardingStateMachineProvider

  // NOTE: Questionnaire completion navigation for onboarding is now handled by OnboardingStateMachineProvider

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    navigateDashboard(newValue as NavigationSection);
  };

  if (!user || loading) return null;

  return dashboard.affects('create') ||
    dashboard.affects('open') ||
    (navigation.onboarding.inProgress &&
      user.isAnonymous === true &&
      currentState !== OnboardingState.AwaitingEmail &&
      currentState !== OnboardingState.AwaitingEmailConfirmation) ? (
    <>
      <div className="flex items-center justify-center mt-24">
        <Loading />
      </div>
    </>
  ) : (
    <>
      {/* <Navigation /> */}
      <TextReplacer>
        <div
          data-testid="route-dashboard"
          className={cx('2xl:container md:px-7 mb-24', {
            'pb-[65px]': isMobile && false,
          })}
        >
          {/* Grid layout ensures tabs and body have same width */}
          <div
            className={cx(
              'w-full pt-3',
              'md:grid md:grid-cols-[250px_minmax(0,672px)]',
              'lg:grid-cols-[250px_minmax(0,896px)_250px]',
              'xl:grid-cols-[300px_minmax(0,896px)_300px]'
            )}
          >
            {/* Spacer for side menu (tabs row) */}
            <div className={cx('hidden md:block', activeSection === NavigationSection.Status && 'md:hidden')} />

            {/* Tabs (middle column) */}
            <div
              className={cx({
                'fixed bottom-0 inset-x-0 bg-white z-10 shadow-2xl': isMobile && false,
                'col-span-full': activeSection === NavigationSection.Status,
              })}
            >
              <div
                className={cx(
                  '',
                  activeSection !== NavigationSection.Status && 'border-b-gray-300',
                  isMobile && false ? 'border-t' : 'border-b'
                )}
              >
                <Tabs
                  TabIndicatorProps={{
                    className: isMobile && false ? 'top-0 bottom-[unset]' : '',
                  }}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  value={
                    ValidTabValues.includes((navigation.activeSection || '') as NavigationSection)
                      ? navigation.activeSection
                      : false
                  }
                  variant={navigation.isMobile ? 'fullWidth' : undefined}
                >
                  <Tab
                    value={NavigationSection.Overview}
                    disableTouchRipple
                    disableRipple
                    data-testid="tab-button-overview"
                    label={<span className="xl:px-10 px-4 capitalize text-lg">{t('common.overview')}</span>}
                  />
                  <Tab
                    value={NavigationSection.Training}
                    label={
                      <span
                        className="xl:px-10 md:px-4 capitalize text-lg"
                        data-testid="sectionInfo-training-link-button"
                      >
                        {t('common.topic.employees')}
                      </span>
                    }
                    data-testid="tab-button-employees"
                  />
                  {(user.hasFeature('MultiTenancy') || user.companies.length > 1) && (
                    <Tab
                      value={NavigationSection.MultiTenant}
                      data-testid="tab-button-tenants"
                      label={<span className="xl:px-10 px-4 capitalize text-lg">{t('dashboard.multitenant')}</span>}
                    />
                  )}
                  {user.hasFeature('DpoManaged') && (user.role === UserRole.Admin || user.role === UserRole.Dpo) && (
                    <Tab
                      value={NavigationSection.Dpo}
                      data-testid="tab-button-dpo"
                      label={<span className="xl:px-10 px-4 capitalize text-lg">{t('dashboard.dpo.label')}</span>}
                    />
                  )}
                </Tabs>
              </div>
            </div>

            {/* Spacer for info panel (tabs row, hidden below lg) */}
            <div className={cx('hidden lg:block', activeSection === NavigationSection.Status && 'lg:hidden')} />

            {/* Side menu (content row) */}
            <div
              className={cx(
                'xl:min-w-[300px] xl:max-w-[300px] md:min-w-[250px] md:max-w-[250px]',
                activeSection === NavigationSection.Status && 'hidden'
              )}
            >
              {!isMobile && <SideMenuList />}
            </div>

            {/* Body (content row, middle column - same width as tabs) */}
            <div
              className={cx(
                'md:p-12 p-8',
                activeSection == NavigationSection.Status
                  ? 'bg-primary-200 activeSectionStatus col-span-full'
                  : 'bg-white'
              )}
            >
              <SectionBody dashboard={dashboard} />
            </div>

            {/* Info panel (content row, right column) */}
            {activeSection === NavigationSection.Status ? null : activeSection === NavigationSection.Dpo ? (
              <div className="hidden lg:block xl:min-w-[300px] xl:max-w-[300px] md:min-w-[250px] md:max-w-[250px]">
                <DpoSectionInfo />
              </div>
            ) : (
              <div className="hidden lg:block xl:min-w-[300px] xl:max-w-[300px] md:min-w-[250px] md:max-w-[250px]">
                <SectionInfo />
              </div>
            )}
          </div>

          <Tutorial />
          <DocumentPaper />
          <Roadblock />
          <RoadblockDSFA />
          <NewServiceMessage />
          <PasswordModal />
          <QuestionnaireChangedModal />
          <FullCompletionMessage />
          <CelloReminderMessage />
          <KlausLaunchMessage />
          <KlausMergeOfferMessage />
          {activeCompany?.id && user?.hasFeature('DpoManaged') && user?.role === UserRole.Admin && (
            <DpoAssignmentModal
              open={!dpoLoading && !dpoInfoData?.getDpoInfo?.isComplete && !dpoModalDismissed}
              onClose={() => setDpoModalDismissed(true)}
              companyId={activeCompany.id}
              forced={true}
            />
          )}
          <Confetti
            shouldFire={navigation.boom}
            bigBlast={true}
            onAnimationStart={() => setNavigation((nav) => void (nav.boom = false))}
          />
          <Cello />
          <Chat />
          <ChangelogSheet
            open={!!navigation.changelogOpen}
            onOpenChange={(open) => setNavigation((nav) => void (nav.changelogOpen = open))}
          />
        </div>
      </TextReplacer>
      {/* EmailModal controlled by onboarding state machine */}
      <EmailModal
        open={
          currentState === OnboardingState.AwaitingEmail || currentState === OnboardingState.AwaitingEmailConfirmation
        }
      />
      {/* we show the blurry backdrop before user has entered his email */}
      {(currentState === OnboardingState.AwaitingEmail ||
        currentState === OnboardingState.AwaitingEmailConfirmation) && (
        <div className="fixed h-full w-full backdrop-blur-sm z-40 top-0 left-0" />
      )}
    </>
  );
}
